import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaDownload, FaSync, FaFilePdf, FaFileExcel, FaTable, FaFilter } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ReportDataViewer from './ReportDataViewer';
import AppToast from '../AppToast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './css/ReportViewer.css';

const ReportViewer = ({
  reportId: propReportId,
  embed = false,
  onError,
  onReportLoaded,
  initialDataLimit = 100000
} = {}) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const routeReportId = params?.reportId;
  const resolvedReportId = propReportId || routeReportId;

  const [reportInfo, setReportInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataLimit, setDataLimit] = useState(initialDataLimit);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [chartsCount, setChartsCount] = useState(0);

  const reportDataViewerRef = useRef(null);

  useEffect(() => {
    setDataLimit(initialDataLimit);
  }, [initialDataLimit]);

  // Helper function to show toast
  const showToast = (message, variant = 'success') => {
    console.log('Showing toast:', { message, variant });
    setToast({ show: true, message, variant });
  };

  // Helper function to hide toast
  const hideToast = () => {
    setToast({ show: false, message: '', variant: 'success' });
  };

  useEffect(() => {
    if (resolvedReportId) {
      loadReportInfo(resolvedReportId);
    } else if (embed) {
      const message = 'No report selected';
      setLoadError(message);
      if (typeof onError === 'function') {
        onError(message);
      }
    } else {
      showToast('No report ID provided', 'danger');
      navigate('/reports');
    }
  }, [resolvedReportId, embed]);


  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  const loadReportInfo = async (id) => {
    setLoading(true);
    setLoadError(null);
    try {
      const reportBuilderApi = new DataApi('reportbuilder');
      const response = await reportBuilderApi.get(`/${id}`);

      if (response?.data?.success) {
        const report = response.data.data;
        console.log('Loaded report info for viewer:', report);

        // Set report information
        const info = {
          id: report.id,
          name: report.name,
          report_label: report.report_label,
          description: report.description,
          object_name: report.object_name,
          object_label: report.object_label,
          report_type: report.report_type,
          is_public: report.is_public,
          created_date: report.created_date,
          last_modified_date: report.last_modified_date,
          report_objects: report.report_objects || []
        };

        setReportInfo(info);
        if (typeof onReportLoaded === 'function') {
          onReportLoaded(info);
        }

      } else {
        throw new Error('Failed to load report');
      }
    } catch (error) {
      console.error('Error loading report:', error);
      const message = error?.message || 'Failed to load report. Please try again.';
      setReportInfo(null);
      if (embed) {
        setLoadError(message);
        if (typeof onError === 'function') {
          onError(message);
        }
      } else {
        showToast(message, 'danger');
        navigate('/reports');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    // Call the refresh method on ReportDataViewer
    if (reportDataViewerRef.current && reportDataViewerRef.current.refreshData) {
      reportDataViewerRef.current.refreshData();
      if (!embed) {
        showToast('Refreshing report data...', 'info');
      }
    }
  };

  const handleFilterToggle = () => {
    setShowFilters(!showFilters);
  };

  const handleFiltersLoaded = (count) => {
    console.log('ReportViewer: Filters loaded, count:', count);
    setFilterCount(count);
  };

  const handleChartsLoaded = (count) => {
    console.log('ReportViewer: Charts loaded, count:', count);
    setChartsCount(count);
  };

  const handleExport = (format) => {
    // Call the export method on ReportDataViewer
    if (reportDataViewerRef.current && reportDataViewerRef.current.exportData) {
      reportDataViewerRef.current.exportData(format);
      setShowExportMenu(false);
    }
  };

  const handleLimitChange = (newLimit) => {
    setDataLimit(newLimit);
    if (!embed) {
      showToast(`Limit changed to ${newLimit.toLocaleString()} records`, 'success');
    }
  };

  const handleBack = () => {
    if (embed) {
      return;
    }
    // Check if we came from reportbuilder or reports list
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get('source');

    if (source === 'reportbuilder') {
      // Go back to reportbuilder page with the report ID
      navigate(`/reportbuilderpage/${resolvedReportId}`);
    } else {
      // Default: go back to reports list
      navigate('/reports');
    }
  };

  if (loading) {
    return (
      <div className={`report-viewer-page ${embed ? 'report-viewer-embed' : ''}`}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`report-viewer-page ${embed ? 'report-viewer-embed' : ''}`}>
        <div className="error-container">
          <h2>Unable to Load Report</h2>
          <p>{loadError}</p>
          {!embed && (
            <button onClick={handleBack} className="btn btn-primary">
              Back to Reports
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!reportInfo) {
    return (
      <div className={`report-viewer-page ${embed ? 'report-viewer-embed' : ''}`}>
        <div className="error-container">
          <h2>Report Not Found</h2>
          <p>The requested report could not be found.</p>
          {!embed && (
            <button onClick={handleBack} className="btn btn-primary">
              Back to Reports
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`report-viewer-page ${embed ? 'report-viewer-embed' : ''}`}>
      {embed && reportInfo && (
        <div className="report-viewer-embed-toolbar">
          <div className="action-buttons">
            {filterCount > 0 && (
              <button
                type="button"
                className="action-btn"
                onClick={handleFilterToggle}
                title="Report filters"
                style={{
                  backgroundColor: showFilters ? 'var(--primary-color)' : '',
                  color: showFilters ? 'white' : ''
                }}
              >
                <FaFilter />
              </button>
            )}
            <button
              type="button"
              className="action-btn"
              onClick={handleRefresh}
              title="Refresh data"
            >
              <FaSync />
            </button>
          </div>
          <div className="data-controls report-viewer-embed-data-controls">
            <label htmlFor="embed-dataLimit">Limit:</label>
            <select
              id="embed-dataLimit"
              value={dataLimit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
              className="limit-select"
            >
              <option value={1000}>1,000</option>
              <option value={5000}>5,000</option>
              <option value={10000}>10,000</option>
              <option value={50000}>50,000</option>
              <option value={100000}>100,000</option>
              <option value={500000}>500,000</option>
            </select>
          </div>
        </div>
      )}
      {!embed && (
        <div className="report-viewer-header">
          <div className="report-title-section">
            <button className="back-button" onClick={handleBack}>
              <FaArrowLeft />
            </button>
            <div className="report-title-info">
              <h2 className="report-title">{reportInfo.report_label || reportInfo.name}</h2>
              <div className="report-meta">
                <span className="report-type">{reportInfo.report_type}</span>
                <span className="report-object">
                  {reportInfo.report_objects && reportInfo.report_objects.length > 0 ? (
                    // Show all objects from report_objects array (including primary)
                    reportInfo.report_objects.map((obj, index) => (
                      <span key={obj.id || index}>
                        {obj.object_label || obj.object_name}
                        {index < reportInfo.report_objects.length - 1 && ' & '}
                      </span>
                    ))
                  ) : (
                    // Fallback to primary object only
                    reportInfo.object_label || reportInfo.object_name
                  )}
                </span>
                {reportInfo.description && (
                  <span className="report-description">{reportInfo.description}</span>
                )}
              </div>
            </div>
          </div>

          <div className="report-viewer-actions">
            {/* Filter, Export and Refresh buttons */}
            <div className="action-buttons">
              {/* Filter button - only show if ReportDataViewer has filters */}
              {filterCount > 0 && (
                <button
                  className="action-btn"
                  onClick={handleFilterToggle}
                  title="Report Filters"
                  style={{
                    backgroundColor: showFilters ? 'var(--primary-color)' : '',
                    color: showFilters ? 'white' : ''
                  }}
                >
                  <FaFilter />
                </button>
              )}

              <button
                className="action-btn"
                onClick={handleRefresh}
                title="Refresh Data"
              >
                <FaSync />
              </button>

              <div className="export-dropdown">
                <button
                  className="action-btn"
                  title="Export Data"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                >
                  <FaDownload />
                </button>
                {showExportMenu && (
                  <div className="export-menu">
                    <button
                      className="export-option"
                      onClick={() => handleExport('excel')}
                      title="Export Simple Data to Excel"
                    >
                      <FaFileExcel /> Export Excel
                    </button>
                    <button
                      className="export-option"
                      onClick={() => handleExport('formatted-excel')}
                      title="Export Formatted Excel with Subtotals & Grand Totals"
                    >
                      <FaTable /> Export Formatted Excel
                    </button>
                    <button
                      className="export-option"
                      onClick={() => handleExport('pdf')}
                      title="Export as PDF"
                    >
                      <FaFilePdf /> Export as PDF
                    </button>
                    {chartsCount > 0 && (
                      <button
                        className="export-option"
                        onClick={() => handleExport('pdf-with-charts')}
                        title="Export PDF with Charts"
                      >
                        <FaFilePdf /> Export PDF with Charts
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="data-controls">
              <label htmlFor="dataLimit">Records Limit:</label>
              <select
                id="dataLimit"
                value={dataLimit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                className="limit-select"
              >
                <option value={1000}>1,000</option>
                <option value={5000}>5,000</option>
                <option value={10000}>10,000</option>
                <option value={50000}>50,000</option>
                <option value={100000}>100,000</option>
                <option value={500000}>500,000</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="report-viewer-content">
        <ReportDataViewer
          ref={reportDataViewerRef}
          reportId={resolvedReportId}
          dataLimit={dataLimit}
          showFilters={showFilters}
          onFilterToggle={handleFilterToggle}
          onFiltersLoaded={handleFiltersLoaded}
          onChartsLoaded={handleChartsLoaded}
        />
      </div>

      {/* AppToast for notifications */}
      {!embed && (
        <AppToast
          show={toast.show}
          message={toast.message}
          variant={toast.variant}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default ReportViewer;
